import { useEffect, useState } from "react";
import { openOrDownloadFile } from "@/Shared/Helpers/util.helper";
import { ResponseHook } from "../../../../Shared/Logic/Hooks/ResponseHook";
import { DOMAIN_CONFIGS } from "@/Shared/Config/domain-config";

import {
    ActionResponse,
    DomainType,
    EXPORT_FORMATS,
    ExportAction,
    ExportParams,
    ExportType,
    FeedBackType,
    GenerateAction,
    UIFeedbackHooks
} from "@/Shared/Types";

import {
    ActionStatusHelper,
    ErrorResponseHelper,
    MessageHelper
} from "@/Shared/Helpers";


export type ExportFormat = typeof EXPORT_FORMATS[keyof typeof EXPORT_FORMATS];


export interface ExportHook {
    exportDocumentResponse: ActionResponse,
    exportExcel: <T>(type: ExportType, params?: ExportParams<T>) => Promise<void>,
    executeExportPDF: <T>(type: ExportType, params?: ExportParams<T>,) => Promise<void>,
    exportCsv: <T>(type: ExportType, params?: ExportParams<T>) => Promise<void>,
}

/**
 * useExport hook – handles data export for a given resource.
 * 
 * Author: Samano CASTRE  
 * Date: January 7, 2026
 * 
 * Responsibilities:
 * - Provides a unified export mechanism (Excel, CSV, PDF) for any resource type
 * - Manages export request lifecycle (loading, success, error)
 * - Triggers binary file download in the browser
 * - Exposes user feedback state (toast) to the component layer
 * 
 * Behavior:
 * - Prevents concurrent export executions
 * - Calls the export service with optional filters and sorting
 * - Automatically resolves file extension based on export format
 * - Emits reactive toast messages based on export state changes
 */
export function useExportDocument(
    domainType: DomainType,
    uiFeedbackHooks: UIFeedbackHooks = {} as UIFeedbackHooks,
    generateAction: GenerateAction<any>,
    exportAction: ExportAction<any>,
): ExportHook {

    const { toastHook, confirmHook } = uiFeedbackHooks;
    const domainLabel = DOMAIN_CONFIGS[domainType].domainLabel;
    /**
     * ActionResponse state used to track the export request status
     * (idle, loading, success, error).
     */
    const [action, setAction] = useState<GenerateAction<any>|ExportAction<any>>();

    //const [exportDocumentResponse, setExportDocumentResponse] = useState<ActionResponse>({ status: 'idle' });

    useEffect(()=> {
        if(!action) return;
        const {isLoading, isFailed, isSucceeded} = ActionStatusHelper.is(action.response.status)
        if(isLoading) {
            toastHook.openToast({
                data: {
                    message: MessageHelper.EXPORTING.LOADING(domainLabel),
                    type: FeedBackType.INFO
                }
            });
        }
        if(isFailed) {
            toastHook.openToast({
                data: {
                    message: ErrorResponseHelper.getError(action.response.error, MessageHelper.EXPORTING.ERROR(domainLabel)).error,
                    type: FeedBackType.ERROR
                }
            });
        }
        if(isSucceeded) {
            toastHook.openToast({
                data: {
                    message: MessageHelper.EXPORTING.SUCCESS(domainLabel),
                    type: FeedBackType.SUCCESS
                },
            });
        }

    },[action]);


    /**
     * Core export handler.
     *
     * - Prevents concurrent exports
     * - Triggers the export service
     * - Resolves the file format and extension
     * - Downloads the binary file
     * - Updates the export response state accordingly
     */
    const FileExport = async (type: ExportType, format: ExportFormat, params: ExportParams<any> = {}) => {
        if(action && ActionStatusHelper.isLoading(action.response.status)) {
            return;
        }

        let confirmMessage = MessageHelper.MODAL.CONFIRM.EXPORTING.DEFAULT(domainLabel);

        switch (format) {
            case EXPORT_FORMATS.EXCEL:
                confirmMessage = MessageHelper.MODAL.CONFIRM.EXPORTING.EXCEL(domainLabel);
                break;
            case EXPORT_FORMATS.PDF:
                confirmMessage = MessageHelper.MODAL.CONFIRM.EXPORTING.PDF(domainLabel);
                break;
            default:
                console.warn("Format d'export inconnu");
                break;
        }


        const confirmed = await confirmHook.openConfirm?.({
            title: MessageHelper.MODAL.CONFIRM.TITLE,
            content: confirmMessage,
        });

        if (confirmed) {
            const userAction = type === "generate" ? generateAction : exportAction;
            ResponseHook.loading(userAction.setResponse);
            setAction(userAction);

            const data = await userAction.execute?.(params, format)

            let extension = '.txt';
            switch (format) {
                case EXPORT_FORMATS.EXCEL:
                    extension = 'xlsx';
                    break;
                case EXPORT_FORMATS.CSV:
                case EXPORT_FORMATS.PDF:
                    extension = format;
                    break;
                default:
                    extension = 'txt';
                    break;
            }

            if(data) {
                openOrDownloadFile(
                    data.blob, 
                    data.filename ?? domainType + "-" + new Date().toISOString() + "." + extension
                );
            }
            
        }
    };

    /**
     * Public API exposed by the hook.
     *
     * Consumers can:
     * - trigger exports by format
     * - access export state
     * - control toast visibility
     */
    return {
        exportDocumentResponse : action?.response ?? {status : 'idle'},
        exportExcel: <T>(type: ExportType, params?: ExportParams<T>) => FileExport(type, EXPORT_FORMATS.EXCEL, params),
        exportCsv: <T>(type: ExportType, params?: ExportParams<T>) => FileExport(type, EXPORT_FORMATS.CSV, params),
        executeExportPDF: <T>(type: ExportType, params?: ExportParams<T>,) => {
            //Update params here :

            return FileExport(type, EXPORT_FORMATS.PDF, params)
        },
    };
}